import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Library, BookmarkPlus, Search, Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * A pair of small icon buttons that sit alongside the voice mic on a
 * textarea header:
 *
 *   📚 (Library)  → opens a searchable dropdown of the user's saved
 *                    prompts. Picking one REPLACES the current textarea
 *                    content (per product spec — load saved prompt UX
 *                    is closer to "open file" than "insert at cursor").
 *   ➕ (BookmarkPlus) → opens an inline name-prompt to save the current
 *                    textarea content as a new entry in the library.
 *
 * The same shared library is exposed by both icons regardless of which
 * textarea hosts them, so a prompt saved in the regen modal shows up
 * in PPT Studio and vice versa.
 *
 * Props
 *   value: string                   — current textarea value
 *   onChange: (next: string) => void
 *   disabled: boolean
 *   testId: string                  — data-testid prefix
 *   prompts: SavedPrompt[]          — list of {id, name, content}
 *   onPromptsChange: () => void     — called after CRUD ops so parent re-fetches
 */
export default function SavedPromptsPicker({
  value,
  onChange,
  disabled = false,
  testId = "saved-prompts",
  prompts = [],
  onPromptsChange,
}) {
  const [open, setOpen] = useState(false);          // dropdown open
  const [savingOpen, setSavingOpen] = useState(false); // save-as popover
  const [search, setSearch] = useState("");
  const [saveName, setSaveName] = useState("");
  const [busy, setBusy] = useState(false);
  const popoverRef = useRef(null);
  const savePopoverRef = useRef(null);

  // Close the popover when clicking outside — same pattern as Radix
  // Popover but without pulling in another dep. Re-bind on every state
  // change because the listener captures `open` by closure.
  useEffect(() => {
    if (!open && !savingOpen) return undefined;
    const onDoc = (e) => {
      const inMain = popoverRef.current && popoverRef.current.contains(e.target);
      const inSave = savePopoverRef.current && savePopoverRef.current.contains(e.target);
      if (!inMain && !inSave) {
        setOpen(false);
        setSavingOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, savingOpen]);

  // Filter prompts client-side — names are short and the list is
  // capped at 100/user so a substring scan per keystroke is cheap.
  // Matches on either name OR content so users can hunt by remembered
  // phrasing too.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return prompts;
    return prompts.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.content || "").toLowerCase().includes(q),
    );
  }, [prompts, search]);

  const handlePick = (p) => {
    onChange(p.content || "");
    setOpen(false);
    setSearch("");
  };

  const handleSave = async () => {
    if (!value.trim()) {
      toast.error("Nothing to save — type something into the textarea first.");
      return;
    }
    const name = saveName.trim();
    if (!name) {
      toast.error("Give this prompt a short name.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/me/prompts`,
        { name, content: value },
        { withCredentials: true },
      );
      toast.success(`Saved "${name}"`);
      setSavingOpen(false);
      setSaveName("");
      onPromptsChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "Couldn't save prompt";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  // The "save" button is disabled until the textarea has something
  // worth saving — prevents the user from creating empty entries that
  // would then need to be deleted.
  const canSave = !!value.trim();

  return (
    <div className="flex items-center gap-2 relative">
      {/* SAVE — labeled button that opens a tiny "name your prompt" popover */}
      <div className="relative" ref={savePopoverRef}>
        <button
          type="button"
          onClick={() => {
            if (!canSave) {
              toast.info("Type something first, then save it as a reusable prompt.");
              return;
            }
            setOpen(false);
            setSavingOpen((v) => !v);
          }}
          disabled={disabled}
          aria-label="Save current text as a prompt"
          title="Save current text as a prompt"
          data-testid={`${testId}-save-btn`}
          className={[
            "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-medium transition-colors flex-shrink-0",
            canSave
              ? "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
              : "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed",
            disabled ? "opacity-40 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <BookmarkPlus className="w-3.5 h-3.5" />
          <span>Save prompt</span>
        </button>
        {savingOpen && (
          <div
            className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-3 space-y-2"
            data-testid={`${testId}-save-popover`}
          >
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Save as a reusable prompt
            </p>
            <input
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              placeholder="e.g., Q4 Market Brief"
              autoFocus
              maxLength={120}
              className="w-full h-9 rounded-md border border-gray-200 px-3 text-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
              data-testid={`${testId}-save-name-input`}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setSavingOpen(false); setSaveName(""); }}
                disabled={busy}
                data-testid={`${testId}-save-cancel`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={busy || !saveName.trim()}
                className="bg-black hover:bg-gray-800"
                data-testid={`${testId}-save-confirm`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* LIBRARY — labeled button that opens the searchable dropdown */}
      <div className="relative" ref={popoverRef}>
        <button
          type="button"
          onClick={() => { setSavingOpen(false); setOpen((v) => !v); }}
          disabled={disabled}
          aria-label="View all saved prompts"
          title="View all saved prompts"
          data-testid={`${testId}-library-btn`}
          className={[
            "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border text-xs font-medium transition-colors flex-shrink-0",
            "bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50",
            disabled ? "opacity-40 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <Library className="w-3.5 h-3.5" />
          <span>View all prompts</span>
          {prompts.length > 0 && (
            <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-gray-100 text-[10px] font-semibold text-gray-600 tabular-nums">
              {prompts.length}
            </span>
          )}
        </button>
        {open && (
          <div
            className="absolute right-0 mt-2 w-80 max-h-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col"
            data-testid={`${testId}-library-popover`}
          >
            {/* Search bar — always visible + focused on open, even
                when the library is empty. Letting users start typing
                immediately (rather than disabling the field) matches
                what they expect from "search-first" UIs like Notion,
                Linear, etc., and the empty state below still tells
                them there's nothing to find yet. */}
            <div className="p-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search saved prompts..."
                  className="w-full h-8 pl-8 pr-3 text-xs rounded-md border border-gray-200 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-300"
                  autoFocus
                  data-testid={`${testId}-library-search`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1.5 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid={`${testId}-library-list`}>
              {filtered.length === 0 ? (
                <div className="p-4 text-center text-xs text-gray-400" data-testid={`${testId}-library-empty`}>
                  {prompts.length === 0
                    ? "No saved prompts yet. Click \"Save prompt\" to add your first one."
                    : "No matches."}
                </div>
              ) : (
                filtered.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handlePick(p)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-b-0 transition-colors"
                    data-testid={`${testId}-library-item-${idx}`}
                  >
                    <div className="text-sm font-medium text-gray-900 truncate">{p.name}</div>
                    <div className="text-[11px] text-gray-500 line-clamp-2 mt-0.5">{p.content}</div>
                  </button>
                ))
              )}
            </div>
            {prompts.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-100 text-[10px] text-gray-400">
                Manage saved prompts in the Personalize tab.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
